<template>
  <h3 class="mt-5">
    {{ $t('changepasswordpage.title') }}
  </h3>
  <Message
    v-if="success"
    severity="success"
    :closable="false"
  >
    {{ $t('changepasswordpage.success') }}
    <router-link to="/">
      {{ $t('loginpage.input.login') }}
    </router-link>
  </Message>
  <form
    v-if="valid"
    class="p-component"
    @submit.prevent="change"
  >
    <div class="p-input mb-3">
      <label
        for="password"
        class="required-input"
      >{{ $t('changepasswordpage.input.password') }}</label>
      <Password
        id="password"
        v-model="v$.password.$model"
        class="col-12 p-0 mt-2"
        :class="{'invalid-input': v$.password.$error }"
        :toggle-mask="true"
        :feedback="false"
        autocomplete="new-password"
      />
      <div
        v-for="passwordError of v$.password.$errors"
        :key="passwordError.$uid"
        class="input-errors"
      >
        <div class="error-msg">
          {{ passwordError.$message }}
          <p v-if="passwordError.$validator === 'passwordPattern'">
            {{ $t('validations.passwordSpecialCharacter') }}: !"#$%&'()*+,-./:;&lt;\?@[]^_
          </p>
        </div>
      </div>
    </div>

    <div class="p-input mb-3">
      <label
        for="passwordConfirmation"
        class="required-input"
      >{{
        $t('changepasswordpage.input.passwordConfirmation')
      }}</label>
      <Password
        id="passwordConfirmation"
        v-model="v$.passwordConfirmation.$model"
        :class="{'invalid-input': v$.password.$error }"
        class="col-12 p-0"
        :toggle-mask="true"
        :feedback="false"
        autocomplete="new-password"
      />
      <div
        v-for="passwordConfirmationError of v$.passwordConfirmation.$errors"
        :key="passwordConfirmationError.$uid"
        class="input-errors"
      >
        <div class="error-msg">
          {{ passwordConfirmationError.$message }}
          <p v-if="passwordConfirmationError.$validator === 'passwordPattern'">
            {{ $t('validations.passwordSpecialCharacter') }}: !"#$%&'()*+,-./:;&lt;\?@[]^_
          </p>
        </div>
      </div>
    </div>

    <Message
      v-if="error"
      severity="error"
      :closable="false"
    >
      <ul class="list-none m-0 p-0">
        <li>
          {{ $t(error) }}
        </li>
      </ul>
    </Message>

    <captcha-component
      ref="captcha"
      v-model:answer="captcha.answer"
      v-model:session="captcha.session"
    />

    <div class="text-right mt-3">
      <div
        v-if="isSaving"
        class="inline-block mr-4"
      >
        <div class="loader-dots">
          <div />
          <div />
          <div />
        </div>
      </div>

      <Button
        type="submit"
        label="Primary"
        class="p-button-rounded p-button-outlined"
        :disabled="changeButtonDisabled"
      >
        {{ $t('changepasswordpage.input.change') }}
      </Button>
    </div>
  </form>
  <div v-else>
    <p> {{ $t('changepasswordpage.expired.message') }} </p>
    <router-link to="/">
      {{ $t('changepasswordpage.expired.home') }}
    </router-link>
  </div>
</template>

<script>
import Button from 'primevue/button'
import Message from "primevue/message";
import Password from "primevue/password";
import {useVuelidate} from "@vuelidate/core";
import {passwordPattern, required, sameAs} from "@/i18n/i18nValidators";
import CaptchaComponent from "@/components/CaptchaComponent.vue";

export default {
  name: 'ChangePasswordForm',
  components: {CaptchaComponent, Button, Message, Password},
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      valid: true,
      password: '',
      passwordConfirmation: '',
      isSaving: false,
      success: false,
      error: '',
      captcha: {
        session: null,
        answer: null
      }
    }
  },
  validations() {
    return {
      password: {required, passwordPattern},
      passwordConfirmation: {
        required, passwordPattern,
        sameAsPassword: sameAs(this.password)
      }
    }
  },
  computed: {
    changeButtonDisabled() {
      return this.v$.$invalid || !this.captcha.answer || !this.captcha.session || this.isSaving;
    },
    token() {
      return this.$route.query.t;
    }
  },
  async mounted() {
    this.valid = await this.$userService.isValid(this.token);
  },
  methods: {
    async change() {
      this.errors = null;
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;

      this.isSaving = true;

      const body = {
        token: this.token,
        password: this.password,
        passwordConfirmation: this.passwordConfirmation,
        captchaSession: this.captcha.session,
        captchaAnswer: this.captcha.answer
      }

      await this.$userService.changePassword(body)
        .then(() => {
          this.error = null;
          this.success = true;
          this.isSaving = false;
        })
        .catch(e => {
          this.captcha.answer = null;
          this.captcha.session = null;
          this.isSaving = false;
          this.$refs.captcha.reload();
          this.error = this.$t(e)
        })
    }
  }
}
</script>

<style scoped lang="scss">

</style>
